import React from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { EngineeringResponse, MathEquation, TableData } from '@/service/api'
import { colors } from '@/styles/colors'
import { useTranslation } from '@/i18n'

// Comprehensive list of LaTeX commands organized by category
// These are commands that ONLY make sense with a backslash prefix
const LATEX_COMMANDS = [
  // Greek letters (lowercase)
  'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'zeta', 'eta', 'theta',
  'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'pi', 'rho', 'sigma',
  'tau', 'upsilon', 'phi', 'chi', 'psi', 'omega',
  // Greek letters (uppercase)
  'Gamma', 'Delta', 'Theta', 'Lambda', 'Xi', 'Pi', 'Sigma', 'Phi', 'Psi', 'Omega',
  // Math functions
  'sqrt', 'frac', 'dfrac', 'tfrac', 'sin', 'cos', 'tan', 'cot', 'sec', 'csc',
  'log', 'ln', 'exp', 'lim', 'sum', 'prod', 'int', 'oint', 'partial',
  // Operators & relations
  'cdot', 'times', 'div', 'pm', 'mp', 'leq', 'geq', 'neq', 'approx',
  'equiv', 'sim', 'propto', 'infty', 'nabla',
  // Formatting
  'text', 'mathrm', 'mathbf', 'mathit', 'boldsymbol', 'left', 'right',
  // Spacing
  'quad', 'qquad', 'hspace', 'vspace',
]

/**
 * Fix common LaTeX escaping issues from model output.
 * The model sometimes outputs `sqrt` instead of `\sqrt` due to JSON escaping issues.
 * This function adds missing backslashes to known LaTeX commands.
 */
const fixLatex = (latex: string): string => {
  let fixed = latex

  // 1. Fix commands followed by { (highest confidence - always LaTeX)
  // Pattern: word{ where word is not preceded by \
  fixed = fixed.replace(/(?<!\\)\b(\w+)(?=\{)/g, (match) => {
    if (LATEX_COMMANDS.includes(match)) {
      return '\\' + match
    }
    return match
  })

  // 2. Fix standalone Greek letters and math symbols (word boundary)
  // Build regex pattern from command list
  const commandPattern = LATEX_COMMANDS.join('|')
  const standaloneRegex = new RegExp(`(?<!\\\\)\\b(${commandPattern})\\b`, 'g')
  fixed = fixed.replace(standaloneRegex, '\\$1')

  // 3. Fix subscripts: C_d → C_{d}, D_pipe → D_{pipe}
  // Pattern: uppercase letter followed by _ and lowercase letter(s)
  // This is safe because it only matches UPPERCASE_lowercase patterns
  // Won't match some_variable (lowercase start) or already braced C_{d}
  fixed = fixed.replace(/([A-Z])_([a-z]+)(?![a-z{])/g, '$1_{$2}')

  return fixed
}

/**
 * Render text with inline LaTeX support.
 * Detects LaTeX patterns like \sqrt{...}, \frac{...}, or $...$ and renders them.
 */
const renderTextWithLatex = (text: string): React.ReactNode[] => {
  // Match LaTeX patterns: $...$ or \command{...} or standalone \command
  const latexPattern = /(\$[^$]+\$|\\[a-zA-Z]+\{[^}]*\}|\\[a-zA-Z]+(?:\s|$))/g
  const parts = text.split(latexPattern)

  return parts.map((part, i) => {
    if (!part) return null

    // Check if this part looks like LaTeX
    if (part.startsWith('$') || part.startsWith('\\')) {
      // Remove $ delimiters if present
      const latex = part.startsWith('$') ? part.slice(1, -1) : part.trim()
      try {
        const html = katex.renderToString(fixLatex(latex), {
          throwOnError: false,
          displayMode: false,
          strict: false,
        })
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />
      } catch {
        // If KaTeX fails, just show the original text
        return <span key={i}>{part}</span>
      }
    }
    return <span key={i}>{part}</span>
  })
}

interface MathRendererProps {
  equation: MathEquation
}

const MathRenderer = ({ equation }: MathRendererProps) => {
  try {
    // Apply LaTeX fixing before rendering
    const fixedLatex = fixLatex(equation.latex)
    const html = katex.renderToString(fixedLatex, {
      displayMode: equation.is_display,
      throwOnError: false,
      errorColor: '#cc0000',
      strict: false,
      trust: false,
    })

    return equation.is_display ? (
      <div
        className="my-2"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    ) : (
      <span dangerouslySetInnerHTML={{ __html: html }} />
    )
  } catch {
    // Fallback if KaTeX fails to parse - show fixed latex for debugging
    const fixedLatex = fixLatex(equation.latex)
    return (
      <code className="bg-gray-100 dark:bg-dark-accent px-1 rounded text-sm">
        {fixedLatex}
      </code>
    )
  }
}

interface DataTableProps {
  data: TableData
}

const DataTable = ({ data }: DataTableProps) => {
  return (
    <div className="my-4 overflow-x-auto">
      {data.caption && (
        <p className="text-sm font-medium mb-2 text-gray-600 dark:text-gray-400">
          {data.caption}
        </p>
      )}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {data.headers.map((header, i) => (
              <th
                key={i}
                className="border px-3 py-2 text-left font-semibold bg-gray-100 dark:bg-dark-accent border-gray-300 dark:border-gray-600"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="border px-3 py-2 border-gray-300 dark:border-gray-600"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface StructuredResponseProps {
  content: EngineeringResponse
}

export const StructuredResponse = ({ content }: StructuredResponseProps) => {
  const { t } = useTranslation()
  return (
    <div className="structured-response space-y-4">
      {/* Problem Summary */}
      <p className="text-base">{content.problem_summary}</p>

      {/* Given Data Table */}
      {content.given_data && <DataTable data={content.given_data} />}

      {/* Calculation Steps */}
      <div className="space-y-4">
        {content.steps.map((step) => (
          <div
            key={step.step_number}
            className="pl-4 border-l-2 border-blue-300 dark:border-blue-700"
          >
            <h4 className="font-semibold text-base mb-1">
              {t('chat:messages.structured.step_label', {
                defaultValue: 'Step {{number}}: {{title}}',
                number: step.step_number,
                title: step.title,
              })}
            </h4>
            <p className="text-sm mb-2 text-gray-700 dark:text-gray-300">
              {renderTextWithLatex(step.explanation)}
            </p>
            {step.equation && <MathRenderer equation={step.equation} />}
            {step.result && (
              <p className="text-sm font-medium mt-1 text-gray-800 dark:text-dark-text">
                <em>{t('chat:messages.structured.result_inline', { defaultValue: 'Result:' })}</em> {step.result}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Final Answer */}
      <div
        className="mt-4 p-4 rounded-lg border-l-4"
        style={{
          backgroundColor: colors.success[50],
          borderColor: colors.success[500]
        }}
      >
        <h4 className="font-bold text-base mb-2" style={{ color: colors.success[800] }}>
          {t('chat:messages.structured.result_heading', { defaultValue: 'Result' })}
        </h4>
        <p className="text-base">{content.final_answer}</p>
        {content.final_equation && (
          <div className="mt-2">
            <MathRenderer equation={content.final_equation} />
          </div>
        )}
      </div>

      {/* Notes / Warnings */}
      {content.notes && (
        <div
          className="mt-4 p-4 rounded-lg border-l-4"
          style={{
            backgroundColor: colors.warning[50],
            borderColor: colors.warning[500]
          }}
        >
          <p className="text-sm" style={{ color: colors.warning[800] }}>
            <strong>{t('chat:messages.structured.note', { defaultValue: 'Note:' })}</strong> {content.notes}
          </p>
        </div>
      )}
    </div>
  )
}

export default StructuredResponse
